import React, { useState, useEffect } from 'react';

const FONT = "'Inter', system-ui, sans-serif";

const Icon = ({ d, size = 20, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ICONS = {
    drivers: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    clients: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
    vendors: 'M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z M3 6h18 M16 10a4 4 0 0 1-8 0',
    fleet: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    members: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
    pie: ['M21.21 15.89A10 10 0 1 1 8 2.83', 'M22 12A10 10 0 0 0 12 2v10z'],
    activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
    arrow: 'M5 12h14M12 5l7 7-7 7',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
    check: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    trend: 'M23 6l-9.5 9.5-5-5L1 18',
};

/* Subtle sparkline decorations for KPI cards */
const sparkPaths = [
    'M0 24 L14 20 L28 26 L42 14 L56 18 L70 8 L84 16 L98 6 L112 12 L120 4',
    'M0 20 L14 26 L28 12 L42 18 L56 8 L70 16 L84 6 L98 14 L112 4 L120 10',
    'M0 14 L14 22 L28 10 L42 24 L56 12 L70 20 L84 8 L98 18 L112 6 L120 16',
];

/* ─── Pie Chart ─── */
function PieChart({ data, size = 150 }) {
    const total = data.reduce((s, d) => s + d.value, 0);
    if (total === 0) return (
        <div style={{ width: size, height: size, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#94a3b8' }}>No data</div>
    );
    const cx = size / 2, cy = size / 2, r = size / 2 - 6;
    let acc = 0;
    const slices = data.map(d => {
        const pct = d.value / total;
        const s = acc * 2 * Math.PI - Math.PI / 2;
        acc += pct;
        const e = acc * 2 * Math.PI - Math.PI / 2;
        return { ...d, path: `M ${cx} ${cy} L ${cx + r * Math.cos(s)} ${cy + r * Math.sin(s)} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${cx + r * Math.cos(e)} ${cy + r * Math.sin(e)} Z`, pct };
    });
    return (
        <svg width={size} height={size} style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.10))' }}>
            {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} opacity={0.92} />)}
            <circle cx={cx} cy={cy} r={r * 0.54} fill="white" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize={18} fontWeight={900} fill="#0f172a" fontFamily={FONT}>{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize={8} fontWeight={700} fill="#94a3b8" fontFamily={FONT} letterSpacing={1.5}>TOTAL</text>
        </svg>
    );
}

/* ─── HBar ─── */
function HBar({ label, value, max, color }) {
    const pct = max === 0 ? 0 : Math.min(100, (value / max) * 100);
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b', fontFamily: FONT }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: FONT }}>{value}</span>
            </div>
            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 1s cubic-bezier(0.4,0,0.2,1)' }} />
            </div>
        </div>
    );
}

export default function OperatorDashboard({ user, onNavigate }) {
    const [stats, setStats] = useState({ drivers: 0, clients: 0, vendors: 0, fleet: 0, members: 0, routes: 0 });
    const [tripCounts, setTripCounts] = useState({ Scheduled: 0, Ongoing: 0, Completed: 0 });
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [headerBg, setHeaderBg] = useState('linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)');
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    useEffect(() => {
        if (!operatorId) return;
        (async () => {
            try {
                const [driversRes, clientsRes, vendorsRes, fleetRes, operatorRes, tripsRes, routesRes, subRes, membersRes] = await Promise.all([
                    fetch(`/api/drivers/${operatorId}`),
                    fetch(`/api/clients/${operatorId}`),
                    fetch(`/api/vendors/${operatorId}`),
                    fetch(`/api/vehicles/${operatorId}`),
                    fetch(`/api/operators/${operatorId}`),
                    fetch(`/api/trips/${operatorId}`),
                    fetch(`/api/routes/${operatorId}`),
                    fetch(`/api/subscriptions/operator/${operatorId}`),
                    fetch(`/api/operators/${operatorId}/members`)
                ]);
                const [drivers, clients, vendors, fleet, operator, trips, routes, subscriptions, members] = await Promise.all([
                    driversRes.json(), clientsRes.json(), vendorsRes.json(), fleetRes.json(), operatorRes.json(), tripsRes.json(), routesRes.json(), subRes.json(), membersRes.json()
                ]);
                setStats({
                    drivers: Array.isArray(drivers) ? drivers.length : 0,
                    clients: Array.isArray(clients) ? clients.length : 0,
                    vendors: Array.isArray(vendors) ? vendors.length : 0,
                    fleet: Array.isArray(fleet) ? fleet.length : 0,
                    members: Array.isArray(members) ? members.length : 0,
                    routes: Array.isArray(routes) ? routes.length : 0,
                });
                if (Array.isArray(subscriptions)) {
                    const activeSub = subscriptions.find(s => s.status === 'Active');
                    if (activeSub) setSubscription(activeSub);
                }
                if (Array.isArray(trips)) {
                    const counts = { Scheduled: 0, Ongoing: 0, Completed: 0 };
                    trips.forEach(t => {
                        const s = t.status || 'Scheduled';
                        if (counts[s] !== undefined) counts[s]++;
                    });
                    setTripCounts(counts);
                }
            } catch (err) {
                console.error('Dashboard fetch error:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, [operatorId]);

    const totalTrips = tripCounts.Scheduled + tripCounts.Ongoing + tripCounts.Completed;
    const networkTotal = Math.max(stats.drivers + stats.clients + stats.vendors + stats.fleet + stats.members, 1);

    const tripCards = [
        {
            status: 'Scheduled',
            count: tripCounts.Scheduled,
            icon: ICONS.clock,
            desc: 'Upcoming trips ready to go',
        },
        {
            status: 'Ongoing',
            count: tripCounts.Ongoing,
            icon: ICONS.bolt,
            desc: 'Currently in progress',
        },
        {
            status: 'Completed',
            count: tripCounts.Completed,
            icon: ICONS.check,
            desc: 'Successfully finished',
        },
    ];

    const kpis = [
        { label: 'Total Drivers', value: stats.drivers, icon: ICONS.drivers, color: '#3b82f6', bg: 'white', border: '#e2e8f0' },
        { label: 'Active Clients', value: stats.clients, icon: ICONS.clients, color: '#3b82f6', bg: 'white', border: '#e2e8f0' },
        { label: 'Vendors & Partners', value: stats.vendors, icon: ICONS.vendors, color: '#3b82f6', bg: 'white', border: '#e2e8f0' },
        { label: 'Managed Fleet', value: stats.fleet, icon: ICONS.fleet, color: '#3b82f6', bg: 'white', border: '#e2e8f0' },
        { label: 'Team Members', value: stats.members, icon: ICONS.members, color: '#3b82f6', bg: 'white', border: '#e2e8f0' },
    ];

    const pieData = [
        { label: 'Drivers', value: stats.drivers, color: '#1e3a8a' },
        { label: 'Clients', value: stats.clients, color: '#1e40af' },
        { label: 'Vendors', value: stats.vendors, color: '#1d4ed8' },
        { label: 'Fleet', value: stats.fleet, color: '#2563eb' },
        { label: 'Members', value: stats.members, color: '#60a5fa' },
    ];

    const efficiency = [
        { label: 'Fleet Utilisation', value: stats.fleet > 0 ? Math.min(100, Math.round((stats.drivers / Math.max(stats.fleet, 1)) * 100)) : 0, color: '#3b82f6' },
        { label: 'Driver Coverage', value: stats.clients > 0 ? Math.min(100, Math.round((stats.drivers / Math.max(stats.clients, 1)) * 100)) : 0, color: '#3b82f6' },
        { label: 'Vendor Diversity', value: stats.vendors > 0 ? Math.min(100, Math.round((stats.vendors / Math.max(stats.fleet, 1)) * 100)) : 0, color: '#3b82f6' },
        { label: 'Team Ratio', value: stats.members > 0 ? Math.min(100, Math.round((stats.members / Math.max(stats.drivers + stats.clients, 1)) * 100)) : 0, color: '#3b82f6' },
    ];

    return (
        <div style={{ background: '#f1f5f9', minHeight: '100vh', fontFamily: FONT }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.6} 70%{transform:scale(1.6);opacity:0} 100%{transform:scale(1);opacity:0} }
                .trip-card { cursor: pointer; transition: transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s ease; }
                .trip-card:hover { transform: translateY(-6px) scale(1.02); }
                .kpi-card { transition: transform 0.2s ease, box-shadow 0.2s ease; animation: fadeUp 0.4s ease both; }
                .kpi-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px -8px rgba(0,0,0,0.13) !important; }
                .dash-section { animation: fadeUp 0.45s ease both; }
            `}</style>

            {/* ── Hero Header ── */}
            <div style={{ background: headerBg, transition: 'background 0.3s ease', borderBottom: '1px solid #e2e8f0', padding: '36px 44px 100px', position: 'relative', overflow: 'hidden' }}>
                {/* Decorative blobs */}
                <div style={{ position: 'absolute', top: -60, right: -60, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)' }} />
                <div style={{ position: 'absolute', bottom: -80, left: 80, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,197,94,0.05) 0%, transparent 70%)' }} />
                <div style={{ position: 'absolute', top: 40, left: '40%', width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 70%)' }} />

                <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>Analytics Overview</div>
                        <h1 style={{ fontSize: 32, fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.1 }}>Operational Dashboard</h1>
                        <p style={{ color: '#64748b', fontSize: 14, margin: '10px 0 0', fontWeight: 500 }}>Real-time insights across your logistics network</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 600, color: '#0f172a', backdropFilter: 'blur(8px)' }}>
                        <div style={{ position: 'relative', width: 8, height: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', position: 'absolute' }} />
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', position: 'absolute', animation: 'pulse-ring 1.8s ease infinite' }} />
                        </div>
                        Live Data
                    </div>
                </div>

                {/* Trip Summary Row inside hero */}
                <div style={{ position: 'relative', zIndex: 2, display: 'flex', gap: 16, marginTop: 32, flexWrap: 'wrap' }}>
                    {['Scheduled', 'Ongoing', 'Completed'].map((s, i) => {
                        return (
                            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#0f172a', animationDelay: `${i * 0.08}s` }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>{s}:</div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: '#1e3a8a', background: '#f1f5f9', padding: '2px 12px', borderRadius: 20, border: '1px solid #e2e8f0' }}>{tripCounts[s]}</div>
                                {i < 2 && <div style={{ width: 1, height: 20, background: '#e2e8f0', marginLeft: 4 }} />}
                            </div>
                        );
                    })}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>Total:</div>
                        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{totalTrips}</div>
                    </div>
                </div>
            </div>

            {/* ── Main Content pulled up over hero ── */}
            <div style={{ padding: '0 44px 44px', marginTop: -60 }}>

                {/* Trip Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 28 }}>
                    {tripCards.map((tc, i) => (
                        <div
                            key={tc.status}
                            className="trip-card dash-section"
                            onClick={() => onNavigate && onNavigate('operator-trip-list', tc.status)}
                            style={{ background: 'white', border: '1px solid #1e3a8a', borderRadius: 20, padding: '26px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', animationDelay: `${i * 0.08}s` }}
                        >
                            {/* Card decorations */}
                            <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(30,58,138,0.03)' }} />
                            <div style={{ position: 'absolute', bottom: -30, right: 30, width: 80, height: 80, borderRadius: '50%', background: 'rgba(30,58,138,0.02)' }} />

                            <div style={{ position: 'relative', zIndex: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
                                    <div style={{ width: 48, height: 48, borderRadius: 14, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #f1f5f9' }}>
                                        <Icon d={tc.icon} size={22} color="#1e3a8a" />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f8fafc', borderRadius: 20, padding: '4px 12px', fontSize: 11.5, fontWeight: 700, color: '#1e3a8a', border: '1px solid #f1f5f9' }}>
                                        View All <Icon d="M5 12h14M12 5l7 7-7 7" size={12} color="#1e3a8a" />
                                    </div>
                                </div>
                                <div style={{ fontSize: 48, fontWeight: 900, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.04em', marginBottom: 6 }}>
                                    {loading ? '—' : tc.count}
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                                    {tc.status} Trips
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{tc.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
                    {loading
                        ? [1, 2, 3, 4, 5].map(i => (
                            <div key={i} style={{ background: 'white', borderRadius: 18, padding: 20, height: 110, background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                        ))
                        : kpis.map((k, i) => (
                            <div key={k.label} className="kpi-card" style={{ background: k.bg, border: `1.5px solid ${k.border}`, borderLeft: '4px solid #1e3a8a', borderRadius: 18, padding: '20px 20px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', animationDelay: `${0.2 + i * 0.06}s`, position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'relative', zIndex: 2 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 14px ${k.color}25`, marginBottom: 14 }}>
                                        <Icon d={k.icon} size={18} color={k.color} />
                                    </div>
                                    <div style={{ fontSize: 32, fontWeight: 900, color: k.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{k.value}</div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k.label}</div>
                                </div>
                            </div>
                        ))
                    }
                </div>

                {/* Subscription Details Row */}
                {subscription && subscription.planId && (
                    <div style={{ marginBottom: 20 }}>
                        <div className="dash-section" style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: '26px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', animationDelay: '0.25s' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" size={16} color="#8b5cf6" />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Current Subscription: <span style={{ color: '#00B2A9' }}>{subscription.planId.name}</span></div>
                                        <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}>
                                            Valid until: {new Date(subscription.nextBillingDate || subscription.startDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ background: '#ecfdf5', color: '#059669', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>Active Plan</div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                                {[
                                    { label: 'Drivers Limit', current: stats.drivers, limit: subscription.planId.driversLimit !== undefined ? subscription.planId.driversLimit : subscription.planId.operatorsLimit || 'Unlimited', color: '#3b82f6' },
                                    { label: 'Fleet Limit', current: stats.fleet, limit: subscription.planId.fleetLimit || 'Unlimited', color: '#3b82f6' },
                                    { label: 'Users Limit', current: stats.members, limit: subscription.planId.usersLimit || 'Unlimited', color: '#3b82f6' },
                                    { label: 'Routes Limit', current: stats.routes, limit: subscription.planId.routesLimit || 'Unlimited', color: '#3b82f6' } 
                                ].map(item => {
                                    const isUnlimited = item.limit === 'Unlimited' || item.limit === -1 || item.limit === '-1' || item.limit === null;
                                    const limitVal = isUnlimited ? Infinity : Number(item.limit);
                                    const displayLimit = isUnlimited ? '∞' : limitVal;
                                    const pct = isUnlimited ? 0 : Math.min(100, (item.current / limitVal) * 100);
                                    
                                    return (
                                        <div key={item.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '16px', border: '1px solid #f1f5f9' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{item.label}</span>
                                                <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{item.current} / <span style={{ color: '#94a3b8' }}>{displayLimit}</span></span>
                                            </div>
                                            {!isUnlimited && (
                                                <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', width: `${pct}%`, background: pct > 90 ? '#ef4444' : pct > 75 ? '#f59e0b' : item.color, borderRadius: 99, transition: 'width 1s ease' }} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Middle Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

                    {/* Network Distribution */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: '26px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', animationDelay: '0.3s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={ICONS.pie} size={16} color="#0ea5e9" />
                            </div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Network Distribution</div>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}>All entities breakdown</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                            <PieChart data={pieData} size={160} />
                            <div style={{ flex: 1 }}>
                                {pieData.map(d => (
                                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{d.label}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <span style={{ fontSize: 14, fontWeight: 800, color: d.color }}>{d.value}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', background: '#f8fafc', padding: '1px 6px', borderRadius: 8 }}>
                                                {networkTotal > 0 ? Math.round((d.value / networkTotal) * 100) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Network Strength */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: '26px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', animationDelay: '0.35s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={ICONS.activity} size={16} color="#22c55e" />
                            </div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Network Strength</div>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}>Comparative unit count</div>
                            </div>
                        </div>
                        {[
                            { label: 'Drivers', value: stats.drivers, color: '#1e3a8a' },
                            { label: 'Clients', value: stats.clients, color: '#1e40af' },
                            { label: 'Vendors', value: stats.vendors, color: '#1d4ed8' },
                            { label: 'Fleet Vehicles', value: stats.fleet, color: '#2563eb' },
                            { label: 'Team Members', value: stats.members, color: '#60a5fa' },
                        ].map(d => <HBar key={d.label} {...d} max={networkTotal} />)}
                    </div>
                </div>

                {/* Bottom Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20 }}>

                    {/* Efficiency Metrics */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: '26px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', animationDelay: '0.4s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={ICONS.zap} size={16} color="#f59e0b" />
                            </div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Efficiency Metrics</div>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}>Calculated from live data</div>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            {efficiency.map(m => (
                                <div key={m.label} style={{ background: '#fafafa', borderRadius: 14, padding: '16px 18px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>{m.label}</div>
                                    <div style={{ height: 5, background: '#e2e8f0', borderRadius: 99, marginBottom: 10, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${m.value}%`, background: m.color, borderRadius: 99, transition: 'width 1s ease' }} />
                                    </div>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: m.color, letterSpacing: '-0.03em' }}>{m.value}<span style={{ fontSize: 14 }}>%</span></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Summary Dark Card -> Light Card */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 20, padding: '28px', color: '#0f172a', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden', animationDelay: '0.45s' }}>
                        <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(59,130,246,0.05)' }} />
                        <div style={{ position: 'absolute', bottom: -50, right: 40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(34,197,94,0.05)' }} />
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Platform Health</div>
                            <div style={{ fontSize: 21, fontWeight: 900, marginBottom: 4 }}>Network Summary</div>
                            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 24 }}>Across all connected entities</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {[
                                    { label: 'Total Entities', value: networkTotal, color: '#0f172a' },
                                    { label: 'Fleet Units', value: stats.fleet, color: '#0f172a' },
                                    { label: 'Human Resources', value: stats.drivers + stats.members, color: '#0f172a' },
                                    { label: 'Client Base', value: stats.clients + stats.vendors, color: '#0f172a' },
                                ].map(item => (
                                    <div key={item.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 16px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{item.label}</div>
                                        <div style={{ fontSize: 28, fontWeight: 900, color: '#3b82f6', letterSpacing: '-0.03em' }}>{item.value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 18, padding: '11px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.2)', flexShrink: 0 }} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>All systems operational · {totalTrips} trips tracked</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
